import React, { useEffect, useState, useMemo } from 'react';
import AppLayout from '../../../layout/components/AppLayout';
import documentServices from '../services/documentServices';
import DateSelect2 from '../../../components/AppInput/DateSelect2';
import { useAppData } from '../../../context/AppDataContext';
import CounterData from '../components/dashboard/CounterData';
import DocumentPieChart from '../components/dashboard/DocumentPieChart';
import StaffPieChart from '../components/dashboard/StaffPieChart';
import DocumentBarChart from '../components/dashboard/DocumentBarChart';
import DocumentForm from '../components/DocumentForm';


function DocumentDashboardPage() {
    const [filter, setFilter] = useState(null);
    const [documentList, setDocumentList] = useState(null);
    const {masters} = useAppData();

    const getDocumentList = () => {
        if (filter !== null) {
            documentServices.getList(filter).then(res => {
                setDocumentList(res.data);
            }).catch(error => {
                console.log(error);
            });
        }
    };

    // --- REPORT 1: Standard Totals ---
    const report = useMemo(() => {
        if (!documentList) {
            return { documentStatus: [], assignedTo: [], category: [], department: [], documentType: [] };
        }

        const generateSummaryWithTotals = (masterArray, docKey, idPropName) => {
            let runningTotal = 0;

            const summaryList = masterArray.map(masterItem => {
                const matchCount = documentList.filter(doc => Number(doc[docKey]) === masterItem.value).length;
                runningTotal += matchCount;

                return {
                    [idPropName]: masterItem.value,
                    label: masterItem.label,
                    count: matchCount
                };
            });

            summaryList.push({
                [idPropName]: 'total',
                label: 'Total',
                count: runningTotal,
                isTotalRow: true
            });

            return summaryList;
        };

        const generateAssignmentSummary = () => {
            const counts = documentList.reduce((acc, doc) => {
                const assigned = doc.assignedTo === null ? 'Unassigned' : doc.assignedTo;
                acc[assigned] = (acc[assigned] || 0) + 1;
                return acc;
            }, {});

            let runningTotal = 0;
            const assignmentList = Object.entries(counts).map(([key, value]) => {
                runningTotal += value;
                return {
                    assignedTo: key,
                    label: key === 'Unassigned' ? 'Unassigned' : `User ID ${key}`,
                    count: value
                };
            });

            assignmentList.push({
                assignedTo: 'total',
                label: 'Total',
                count: runningTotal,
                isTotalRow: true
            });

            return assignmentList;
        };

        return {
            documentStatus: generateSummaryWithTotals(masters["document-status"], 'documentStatus', 'status'),
            category: generateSummaryWithTotals(masters["document-category"], 'category', 'category'),
            department: generateSummaryWithTotals(masters["department"], 'department', 'department'),
            documentType: generateSummaryWithTotals(masters["document-type"], 'documentType', 'type'),
            assignedTo: generateAssignmentSummary()
        };
    }, [documentList]);


    // --- REPORT 2: Document Type Matrix Breakdown ---
    const documentTypeMatrix = useMemo(() => {
        if (!documentList) return [];

        return masters["document-type"].map(typeMaster => {
            let typeRowTotal = 0;
            
            const statusBreakdown = masters["document-status"].map(statusMaster => {
                const matchCount = documentList.filter(doc => 
                    Number(doc.documentType) === typeMaster.value && 
                    Number(doc.documentStatus) === statusMaster.value
                ).length;

                typeRowTotal += matchCount;

                return {
                    statusId: statusMaster.value,
                    statusLabel: statusMaster.label,
                    count: matchCount
                };
            });

            return {
                typeId: typeMaster.value,
                typeLabel: typeMaster.label,
                statuses: statusBreakdown,
                totalCount: typeRowTotal
            };
        });
    }, [documentList]);


    // --- REPORT 3: Department Matrix Breakdown ---
    const departmentMatrix = useMemo(() => {
        if (!documentList) return [];

        return masters["department"].map(deptMaster => {
            let deptRowTotal = 0;
            
            const statusBreakdown = masters["document-status"].map(statusMaster => {
                const matchCount = documentList.filter(doc => 
                    Number(doc.department) === deptMaster.value && 
                    Number(doc.documentStatus) === statusMaster.value
                ).length;

                deptRowTotal += matchCount;

                return {
                    statusId: statusMaster.value,
                    statusLabel: statusMaster.label,
                    count: matchCount
                };
            });

            return {
                deptId: deptMaster.value,
                deptLabel: deptMaster.label,
                statuses: statusBreakdown,
                totalCount: deptRowTotal
            };
        });
    }, [documentList]);


    useEffect(() => {
        getDocumentList();
    }, [filter]);

    return (
        <AppLayout>
            <div className='container-fluid'>
                <div className='row mb-4'>
                    <div className='col-md-6'>
                        <h4>File Dashboard</h4>
                    </div>
                    <div className='col-md-6'>
                        <span className='float-end'>
                            <DateSelect2 filter={filter} setFilter={setFilter} />
                            <span className='float-end ms-2'><DocumentForm callBack={getDocumentList} /></span>
                        </span>
                    </div>
                </div>

                {!documentList && (
                    <div className="row">
                        <div className="col-md-12 text-center py-5">
                            <h5>Please select a date filter to load data...</h5>
                        </div>
                    </div>
                )}

                {documentList && (
                    <>
                        <CounterData report={report} documentTypeMatrix={documentTypeMatrix} />
                        <div className='row'>
                            <div className='col-md-4'>
                                 <DocumentBarChart documentTypeMatrix={documentTypeMatrix} /> 
                            </div>
                            <div className='col-md-4'>
                                <DocumentPieChart report={report} />
                            </div>
                            <div className="col-md-4" >
                                <StaffPieChart report={report} />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </AppLayout>
    );
}

export default DocumentDashboardPage;